import * as React from "react";
import { inject, observer } from "mobx-react";
import { MobxComponent } from "../../../../../mobx/components";
import {
	Button,
	FormGroup,
	Input,
	Modal,
	ModalContent,
	ModalTitle,
	SelectAdv,
} from "@lib/components";
import { withTranslation, WithTranslation } from "react-i18next";
import { Formik, FormikActions } from "formik";
import moment from "moment-timezone";
import { FORMATS, Untrusive } from "@lib/common";
import isEmail from "validator/lib/isEmail";
import { UI } from "../../../../../core/ui";
import styled from "styled-components";
import { BookingCustomerSearch } from "./booking-customer-search";
import { loadDashboardBookingTableOptions } from "./booking-modal-table-options";

/** Refreshes table picks when reservation fields change (requires hooks). */
function BookingCreateTableSync({
	depKey,
	onSync,
}: {
	depKey: string;
	onSync: () => void;
}) {
	const cb = React.useRef(onSync);
	cb.current = onSync;
	React.useEffect(() => {
		cb.current();
	}, [depKey]);
	return null;
}

export type BookingGuestPrefill = {
	name: string;
	email: string;
	phone: string;
};

/** Date/time/table from calendar empty-slot click (`YYYY-MM-DD` / `HH:mm`). */
export type CalendarSlotPrefill = { date: string; time: string; table_id?: string };

/** Shared with customer profile → bookings “Create new reservation” handoff. */
export const CW_BOOKING_PREFILL_STORAGE_KEY = "cw_dashboard_booking_prefill_v1";

interface Props extends WithTranslation {
	open: boolean;
	onClose: () => void;
	onCreated: (booking: T.Schema.Booking.BookingSchema) => void;
	/** Seeds guest fields when opening from customer profile (“Create new reservation”). */
	bookingGuestPrefill?: BookingGuestPrefill | null;
	/** Date/time from calendar empty-slot click (YYYY-MM-DD / HH:mm). */
	calendarSlotPrefill?: CalendarSlotPrefill | null;
}

type AdminBookingCreateStatus = "unconfirmed" | "confirmed" | "seated";

interface FormValues {
	name: string;
	email: string;
	phone: string;
	number_of_people: number;
	date: string;
	time: string;
	notes: string;
	/** Empty string = use restaurant default turn in API. */
	turn_minutes: number | "";
	status: AdminBookingCreateStatus;
	/** JSON.stringify(table_ids) from listMoveTableOptions, or "" */
	table_key: string;
}

interface ModalState {
	linkedCustomerId: string | null;
	tableOptions: Array<{ table_ids: string[]; label: string }>;
	tableLoading: boolean;
}

const TwoCol = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 0 1rem;
	padding-bottom: 20px;
	margin-bottom: 20px;
	border-bottom: 1px dashed #e2e8f0;
	@media (max-width: 520px) {
		grid-template-columns: 1fr;
	}
`;

@inject("store")
@observer
export class RestaurantBookingCreateModalClass extends MobxComponent<
	Props,
	ModalState
> {
	constructor(props: Props) {
		super(props);
		this.state = {
			linkedCustomerId: null,
			tableOptions: [],
			tableLoading: false,
		};
	}

	componentDidUpdate(prevProps: Props) {
		if (prevProps.open !== this.props.open) {
			this.setState({
				linkedCustomerId: null,
				tableOptions: [],
				tableLoading: false,
			});
		}
	}

	loadCreateModalTableOptions = async (
		values: FormValues,
		tz: string,
		restaurantId: string
	) => {
		const { store } = this.injected;
		const r = store.restaurant!;
		const bl = r.settings.services.table_booking.booking_logic;
		const defaultTurn =
			bl?.default_turn_minutes && bl.default_turn_minutes > 0
				? bl.default_turn_minutes
				: 90;
		if (values.status !== "confirmed") {
			this.setState({ tableOptions: [], tableLoading: false });
			return;
		}
		const turnRaw = values.turn_minutes;
		const parsed =
			typeof turnRaw === "string" && turnRaw !== ""
				? parseInt(turnRaw, 10)
				: typeof turnRaw === "number"
					? turnRaw
					: NaN;
		const turn =
			Number.isFinite(parsed) && parsed >= 15 ? parsed : defaultTurn;
		const ts = moment
			.tz(`${values.date} ${values.time}`, FORMATS.moment.datetime, tz)
			.valueOf();
		this.setState({ tableLoading: true });
		try {
			const opts = await loadDashboardBookingTableOptions(store, {
				restaurantId,
				timezone: tz,
				timestamp: ts,
				turnMinutes: turn,
				party: values.number_of_people,
			});
			this.setState({ tableOptions: opts, tableLoading: false });
		} catch {
			this.setState({ tableOptions: [], tableLoading: false });
		}
	};

	render() {
		const {
			store,
			t,
			open,
			onClose,
			onCreated,
			bookingGuestPrefill,
			calendarSlotPrefill,
		} = this.injected;
		const r = store.restaurant!;
		const tz = r.settings.region.timezone;
		const service = r.settings.services.table_booking;
		const maxPeople = service.max_people || service.limit_number_of_people;
		const minPeople = service.min_people;

		const defaultDate = moment().tz(tz).format(FORMATS.moment.date);
		const defaultTurnMinutes =
			r.settings.services.table_booking.booking_logic?.default_turn_minutes &&
			r.settings.services.table_booking.booking_logic.default_turn_minutes > 0
				? r.settings.services.table_booking.booking_logic.default_turn_minutes
				: 90;

		const pf = bookingGuestPrefill;
		const slot = calendarSlotPrefill;
		const initial: FormValues = {
			name: (pf?.name && pf.name.trim()) || "",
			email: (pf?.email && pf.email.trim()) || "",
			phone: (pf?.phone && pf.phone.trim()) || "",
			number_of_people: slot
				? Math.max(minPeople || 1, 1)
				: Math.min(maxPeople && maxPeople > 0 ? maxPeople : 8, 4),
			date: (slot?.date && slot.date.trim()) || defaultDate,
			time: (slot?.time && slot.time.trim()) || "18:00",
			notes: "",
			turn_minutes: "",
			status: "confirmed",
			table_key: slot?.table_id ? JSON.stringify([slot.table_id]) : "",
		};

		const submit = async (v: FormValues, form: FormikActions<FormValues>) => {
			let err = "";
			if (!v.name.trim()) err = "required_name";
			if (!v.email.trim()) err = "required_email";
			else if (!isEmail(v.email, { require_tld: true })) err = "invalid_email";
			if (!v.phone.trim()) err = "required_phone";
			if (maxPeople || minPeople) {
				if (
					maxPeople &&
					minPeople &&
					(v.number_of_people > maxPeople || v.number_of_people < minPeople)
				) {
					err = "invalid_people";
				} else if (maxPeople && !minPeople && v.number_of_people > maxPeople) {
					err = "invalid_people";
				} else if (!maxPeople && minPeople && v.number_of_people < minPeople) {
					err = "invalid_people";
				}
			}
			if (err) {
				UI.notification.error(t(`booking.error.${err}`, { defaultValue: err }));
				form.setSubmitting(false);
				return;
			}

			const timestamp = moment
				.tz(`${v.date} ${v.time}`, FORMATS.moment.datetime, tz)
				.valueOf();

			const turnRaw = v.turn_minutes;
			const turnParsed =
				typeof turnRaw === "string" && turnRaw !== ""
					? parseInt(turnRaw, 10)
					: typeof turnRaw === "number"
						? turnRaw
						: NaN;
			const dashboard_turn_minutes =
				Number.isFinite(turnParsed) && turnParsed >= 15
					? turnParsed
					: undefined;

			let dashboard_table_ids: string[] | undefined;
			if (v.status === "confirmed" && v.table_key) {
				try {
					const parsed = JSON.parse(v.table_key) as string[];
					if (Array.isArray(parsed) && parsed.length) {
						dashboard_table_ids = parsed;
					}
				} catch {
					dashboard_table_ids = undefined;
				}
			}

			Untrusive.start();
			form.setSubmitting(true);
			try {
				const res = await store.api.booking_create({
					restaurant_id: r._id,
					booking: {
						notes: v.notes.trim(),
						custom_booking_field_list: [],
						customer: {
							_id: this.state.linkedCustomerId || "",
							name: v.name.trim(),
							phone: v.phone.trim(),
							email: v.email.trim(),
						},
						config: {
							date: moment(timestamp).tz(tz).format(FORMATS.moment.date),
							time: moment(timestamp).tz(tz).format(FORMATS.moment.time),
							timestamp,
							number_of_people: v.number_of_people,
						},
						dashboard_manual_create: true,
						dashboard_initial_status: v.status,
						...(dashboard_turn_minutes !== undefined
							? { dashboard_turn_minutes }
							: {}),
						...(dashboard_table_ids ? { dashboard_table_ids } : {}),
					},
				});
				if (res.outcome !== 0) {
					const msg = (res as { message?: string }).message || "create_failed";
					if (msg === "booking_capacity_threshold") {
						UI.notification.error(
							t("booking.error.capacity_threshold", {
								defaultValue:
									"Restaurant is at capacity for this time (smart threshold). Choose another time.",
							})
						);
						form.setSubmitting(false);
						Untrusive.stop();
						return;
					}
					if (msg === "invalid_table_assignment") {
						UI.notification.error(
							t("booking.error.invalid_tables", {
								defaultValue:
									"Chosen table is not available for this time or party size.",
							})
						);
						form.setSubmitting(false);
						Untrusive.stop();
						return;
					}
					throw new Error(msg);
				}
				if ("booking" in res && res.booking) {
					onCreated(res.booking);
				}
				UI.notification.success(
					t("Booking created", { defaultValue: "Booking created" })
				);
				onClose();
			} catch {
				UI.notification.error(
					t("Could not create booking", { defaultValue: "Could not create booking" })
				);
			} finally {
				form.setSubmitting(false);
				Untrusive.stop();
			}
		};

		return (
			<Modal active={open} close={onClose} width="md" alignTop>
				<ModalTitle className="round-top">
					{pf?.name
						? t("New reservation", { defaultValue: "New reservation" })
						: t("Add booking", { defaultValue: "Add booking" })}
				</ModalTitle>
				<ModalContent paddinglr={24} paddingtb={20}>
					<p className="small text-muted m-b-3 m-t-0">
						{pf?.name
							? t("bookingCreate.fromCustomerSubtitle", {
									defaultValue: "Guest details loaded — pick date & time",
								})
							: t("bookingCreate.subtitle", {
									defaultValue: "Table reservation",
								})}
					</p>
					<Formik initialValues={initial} onSubmit={submit} enableReinitialize>
						{({
							values,
							handleChange,
							handleSubmit,
							isSubmitting,
							setFieldValue,
						}) => {
							const previewTs = moment
								.tz(
									`${values.date} ${values.time}`,
									FORMATS.moment.datetime,
									tz
								)
								.valueOf();

							const statusOpts = [
								{
									value: "unconfirmed",
									label: t(`order.status.unconfirmed`, {
										defaultValue: "Unconfirmed",
									}),
								},
								{
									value: "confirmed",
									label: t(`order.status.confirmed`, {
										defaultValue: "Confirmed",
									}),
								},
								{
									value: "seated",
									label: t(`order.status.seated`, {
										defaultValue: "Seated",
									}),
								},
							];

							const tablePickOpts = [
								{
									value: "",
									label: t("No table (assign later)", {
										defaultValue: "No table (assign later)",
									}),
								},
								...this.state.tableOptions.map((o) => ({
									value: JSON.stringify(o.table_ids),
									label: o.label,
								})),
							];

							const onSyncTables = () => {
								void this.loadCreateModalTableOptions(values, tz, r._id);
							};

							return (
								<form onSubmit={handleSubmit}>
									<BookingCreateTableSync
										depKey={`${values.date}|${values.time}|${values.turn_minutes}|${values.number_of_people}|${values.status}|${open}`}
										onSync={onSyncTables}
									/>
									<p className="small m-b-3 m-t-0">
										{t("datetimeFromTimestamp", { value: previewTs })}
										{" · "}
										{t("number", { value: values.number_of_people })}{" "}
										{t("guests", { defaultValue: "guests" })}
									</p>
									<TwoCol>
										<FormGroup no_border title={t("Date", { defaultValue: "Date" })}>
											<Input
												id="booking-create-date"
												name="date"
												type="date"
												value={values.date}
												onChange={handleChange}
												required
											/>
										</FormGroup>
										<FormGroup no_border title={t("Time", { defaultValue: "Time" })}>
											<Input
												id="booking-create-time"
												name="time"
												type="time"
												value={values.time}
												onChange={handleChange}
												required
											/>
										</FormGroup>
									</TwoCol>
									<BookingCustomerSearch
										active={open}
										restaurantId={r._id}
										showClear={!!this.state.linkedCustomerId}
										onPick={(p) => {
											setFieldValue("name", p.name);
											setFieldValue("email", p.email);
											setFieldValue("phone", p.phone);
											this.setState({ linkedCustomerId: p._id });
										}}
										onClear={() => {
											this.setState({ linkedCustomerId: null });
										}}
									/>
									<FormGroup title={t("Guest name", { defaultValue: "Guest name" })}>
										<Input
											id="booking-create-name"
											name="name"
											value={values.name}
											onChange={handleChange}
											autoComplete="name"
											required
										/>
									</FormGroup>
									<TwoCol>
										<FormGroup no_border title={t("Email", { defaultValue: "Email" })}>
											<Input
												id="booking-create-email"
												name="email"
												type="email"
												value={values.email}
												onChange={handleChange}
												autoComplete="email"
												required
											/>
										</FormGroup>
										<FormGroup no_border title={t("Phone", { defaultValue: "Phone" })}>
											<Input
												id="booking-create-phone"
												name="phone"
												value={values.phone}
												onChange={handleChange}
												autoComplete="tel"
												required
											/>
										</FormGroup>
									</TwoCol>
									<TwoCol>
										<FormGroup no_border title={t("Party size", { defaultValue: "Party size" })}>
											<Input
												id="booking-create-party"
												name="number_of_people"
												type="number"
												min={minPeople || 1}
												max={maxPeople || 99}
												value={values.number_of_people}
												onChange={handleChange}
												required
											/>
										</FormGroup>
										<FormGroup
											no_border
											optional
											title={t("Table", { defaultValue: "Table" })}
										>
											{values.status === "confirmed" &&
											(r.settings.services?.dine_in?.tables || []).length > 0 ? (
												<SelectAdv
													type="single"
													shadow="one"
													value={values.table_key}
													options={tablePickOpts}
													onChange={(v: string) =>
														setFieldValue("table_key", v)
													}
												/>
											) : (
												<Input
													id="booking-create-table"
													value={String(
														t("Confirm booking to assign a table", {
															defaultValue: "Confirm booking to assign a table",
														})
													)}
													disabled
												/>
											)}
										</FormGroup>
									</TwoCol>
									<TwoCol>
										<FormGroup
											no_border
											optional
											title={t("Duration (minutes)", {
												defaultValue: "Duration (minutes)",
											})}
											help={t("bookingCreate.turnHelp", {
												defaultValue:
													"Leave blank to use the default turn from booking settings ({{mins}} min).",
												mins: defaultTurnMinutes,
											})}
										>
											<Input
												id="booking-create-turn"
												name="turn_minutes"
												type="number"
												min={15}
												step={5}
												placeholder={String(defaultTurnMinutes)}
												value={values.turn_minutes}
												onChange={handleChange}
											/>
										</FormGroup>
										<FormGroup
											no_border
											title={t("Booking status", {
												defaultValue: "Booking status",
											})}
										>
											<SelectAdv
												type="single"
												shadow="one"
												value={values.status}
												options={statusOpts}
												onChange={(v: string) => {
													setFieldValue("status", v as AdminBookingCreateStatus);
													setFieldValue("table_key", "");
												}}
											/>
										</FormGroup>
									</TwoCol>
									<FormGroup
										optional
										title={t("Notes", { defaultValue: "Notes" })}
									>
										<Input
											id="booking-create-notes"
											name="notes"
											value={values.notes}
											onChange={handleChange}
											placeholder={t("Optional notes", {
												defaultValue: "Optional notes",
											})}
										/>
									</FormGroup>
									<div className="flex-line gap-2 m-t-3">
										<Button
											type="button"
											color="white"
											full
											onClick={onClose}
											disabled={isSubmitting}
										>
											{t("Cancel", { defaultValue: "Cancel" })}
										</Button>
										<Button
											type="submit"
											color="primary"
											full
											disabled={isSubmitting}
										>
											{t("Create booking", { defaultValue: "Create booking" })}
										</Button>
									</div>
								</form>
							);
						}}
					</Formik>
				</ModalContent>
			</Modal>
		);
	}
}

export const RestaurantBookingCreateModal = withTranslation()(
	RestaurantBookingCreateModalClass
);
